<template>
    <page :busy="busy">
        <b-container fluid class="mb-2">
            <b-row>
                <b-col style="min-height: 300px">
                    <h4 class="title-header">Staylio App Push Notifications</h4>
                    <p class="text-muted">The chart displays all push notifications sent to Staylio; note that inbox-related notifications are not logged and not visible in the grid below.</p>
                    <vue-apex-charts :options="options" :series="series" height="100%" width="100%" />
                </b-col>
            </b-row>
        </b-container>
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :fetch-callback="fetchCallback"
            :is-auto-width="true"
            :getRowStyle="rowStyle"
            :isSearch="true"
            :buttonToolbarClass="['align-items-start flex-md-nowrap']"
            :defaultSort="sort"
            :defaultDesc="true"
            style="min-height: 350px"
        >
            <responsive-button :icon="['fas', 'arrows-rotate']" @click="refresh" variant="primary"
                >Refresh</responsive-button
            >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>

            <div class="merge-source-wrapper mb-0 subscribers-wrapper d-none d-lg-flex mr-1">
                <form-input
                    id="source"
                    @input="(value) => searchSubscribers(value)"
                    :value="subscriberSearch"
                    placeholder="Search by subscriber"
                    autocomplete="off"
                    class="w-100"
                />
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!subscriberSearch && busy">
                    <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="subscribersList.length && subscriberSearch"
                >
                    <div
                        class="guest-item p-2"
                        :key="subscriber.id"
                        v-for="subscriber in subscribersList"
                        @click="selectSubscriber(subscriber.text, subscriber.value)"
                    >
                        <span>{{ subscriber.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="!subscribersList.length && !!subscriberSearch && !selectedSubscriber"
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
                <div v-else />
            </div>

            <div class="merge-source-wrapper mb-0 subscribers-wrapper d-none d-lg-flex mr-1">
                <form-input
                    id="source"
                    autocomplete="off"
                    placeholder="Search by guest"
                    @input="(value) => searchUsers(value)"
                    :value="userSearch"
                    class="w-100"
                />
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!userSearch && busy">
                    <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-else-if="usersList.length && userSearch">
                    <div
                        class="guest-item p-2"
                        :key="user.id"
                        v-for="user in usersList"
                        @click="selectUser(user.text, user.value)"
                    >
                        <span>{{ user.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="!usersList.length && !!userSearch && !selectedUser"
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
                <div v-else />
            </div>

            <b-button-group class="d-lg-none d-block">
                <more-filters-dropdown>
                    <template>
                        <div class="merge-source-wrapper mb-0 subscribers-wrapper d-flex d-lg-none mr-1">
                            <form-input
                                id="source"
                                @input="(value) => searchSubscribers(value)"
                                :value="subscriberSearch"
                                placeholder="Search by subscriber"
                                autocomplete="off"
                                class="w-100"
                            />
                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-if="!!subscriberSearch && busy"
                            >
                                <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                                    <b-spinner variant="primary" :small="true" />
                                </div>
                            </div>
                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-else-if="subscribersList.length && subscriberSearch"
                            >
                                <div
                                    class="guest-item p-2"
                                    :key="subscriber.id"
                                    v-for="subscriber in subscribersList"
                                    @click="selectSubscriber(subscriber.text, subscriber.value)"
                                >
                                    <span>{{ subscriber.text }}</span>
                                </div>
                            </div>
                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-else-if="!subscribersList.length && !!subscriberSearch && !selectedSubscriber"
                            >
                                <div class="p-2">
                                    <span class="text-center">Nothing found</span>
                                </div>
                            </div>
                            <div v-else />
                        </div>
                        <div class="merge-source-wrapper mb-0 subscribers-wrapper d-flex d-lg-none mr-1">
                            <form-input
                                id="source"
                                @input="(value) => searchUsers(value)"
                                :value="userSearch"
                                placeholder="Search by guest"
                                autocomplete="off"
                                class="w-100"
                            />
                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-if="!!subscriberSearch && busy"
                            >
                                <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                                    <b-spinner variant="primary" :small="true" />
                                </div>
                            </div>

                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-else-if="usersList.length && userSearch"
                            >
                                <div
                                    class="guest-item p-2"
                                    :key="user.id"
                                    v-for="user in usersList"
                                    @click="selectUser(user.text, user.value)"
                                >
                                    <span>{{ user.text }}</span>
                                </div>
                            </div>
                            <div
                                class="merge-guests-wrapper subscribers-list-wrapper"
                                v-else-if="!usersList.length && !!userSearch && !selectedUser"
                            >
                                <div class="p-2">
                                    <span class="text-center">Nothing found</span>
                                </div>
                            </div>
                            <div v-else />
                        </div>
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
</template>

<script>
    import debounce from 'lodash/debounce';
    import { mapGetters } from 'vuex';
    import API from '../../../api';
    import {
        STAYLIO_APP_STATISTICS_PUSH_NOTIFICATIONS_ENDPOINT,
        STAYLIO_APP_STATISTICS_USER_ENDPOINT,
    } from '../../../endpoints';
    import { getApexChartsGradientFill } from '../../dashboard/widgets/helpers';
    import Grid from '../../generic/grid/grid.vue';
    import page from '../../generic/page/page.vue';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import PushNotificationsActionCellRenderer from './staylio-app-push-notifications-actions-cell-renderer.vue';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import VueApexCharts from 'vue3-apexcharts';

    export default {
        components: {
            page,
            FormInput,
            Grid,
            ResponsiveButton,
            pushNotificationsActionCellRenderer: PushNotificationsActionCellRenderer,
            DateRangePicker,
            MoreFiltersDropdown,
            VueApexCharts,
        },
        name: 'StaylioAppPushNotificationsStatistic',
        data() {
            return {
                busy: false,
                rowStyle: null,
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [
                    LAST_MONTH,
                    LAST_WEEK,
                    LAST_YEAR,
                    THIS_MONTH,
                    THIS_WEEK,
                    THIS_YEAR,
                    TODAY,
                    YESTERDAY,
                ],

                subscribersList: [],
                usersList: [],
                selectedSubscriber: '',
                selectedUser: '',
                subscriberSearch: '',
                userSearch: '',
                
                chartData: [],
                dates: [],
            };
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol', 'userUnicodeDateFormat', 'userTimeFormat']),

            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },

            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },

            columnDefs() {
                return [
                    {
                        field: 'id',
                        headerName: 'ID',
                        minWidth: 50,
                    },
                    {
                        field: 'subscriber.id',
                        headerName: 'Subscriber ID',
                        minWidth: 150,
                    },
                    {
                        field: 'subscriber.email',
                        headerName: 'Subscriber Email',
                        minWidth: 225,
                    },
                    {
                        field: 'guest.id',
                        headerName: 'Guest ID',
                        minWidth: 120,
                    },
                    {
                        field: 'guest.email',
                        headerName: 'Guest Email',
                        minWidth: 225,
                    },
                    {
                        field: 'mass_push_notification_campaign',
                        headerName: 'Mass Push Notification Campaign ID',
                        minWidth: 190,
                    },
                    {
                        field: 'title',
                        headerName: 'Title',
                        minWidth: 180,
                        maxWidth: 200,
                    },
                    {
                        field: 'message',
                        headerName: 'Message',
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'created',
                        headerName: 'Sent',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                        cellRenderer: this.dateTimeRenderer,
                    },
                    {
                        field: 'delivered_status',
                        headerName: 'Delivered',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                        cellRenderer: this.dateTimeRenderer,
                    },
                    {
                        field: 'read_status',
                        headerName: 'Read',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                        cellRenderer: this.dateTimeRenderer,
                    },
                    {
                        field: 'failed_status',
                        headerName: 'Failed',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                        cellRenderer: this.dateTimeRenderer,
                    },
                    {
                        field: 'guest.app_installed',
                        headerName: 'Staylio Installed',
                        type: 'yesNoColumn',
                        minWidth: 100,
                        sortable: false,
                    },
                    {
                        headerName: '',
                        minWidth: 100,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'pushNotificationsActionCellRenderer',
                        cellRendererParams: {
                            refresh: this.refresh,
                        },
                    },
                ];
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get(`${STAYLIO_APP_STATISTICS_PUSH_NOTIFICATIONS_ENDPOINT()}grid/`, {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            user: this.selectedUser,
                            subscriber: this.selectedSubscriber,
                        },
                    });
                };
            },

            series() {
                return this.chartData.map((item) => {
                    return {
                        name: item.name,
                        data: item.count,
                    };
                });
            },
            options() {
                return {
                    chart: {
                        type: 'area',
                        toolbar: {
                            show: false,
                        },
                        zoom: {
                            enabled: false,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    stroke: {
                        curve: 'straight',
                    },
                    fill: getApexChartsGradientFill(),
                    grid: {
                        xaxis: {
                            lines: {
                                show: true,
                            },
                        },
                    },
                    xaxis: {
                        type: 'datetime',
                        categories: this.dates,
                        labels: {
                            datetimeFormatter: {
                                month: 'MMM',
                                day: 'MMM dd',
                            },
                        },
                    },
                    yaxis: {
                        labels: {
                            formatter: (val) => `${val}`,
                        },
                    },
                    tooltip: {
                        shared: true,
                        intersect: false,
                        custom: (params) => {
                            const { series, seriesIndex, dataPointIndex, w } = params;
                            const day = this.dates[dataPointIndex].split('-')[2];
                            const month = this.dates[dataPointIndex].split('-')[1] - 1;
                            const year = this.dates[dataPointIndex].split('-')[0];
                            let dateFormat = 'dd MMM, yyyy';

                            const date = format(new Date(year, month, day), dateFormat);

                            const filterData = this.chartData.map((item) => {
                                return {
                                    name: item.name,
                                    count: item.count[dataPointIndex],
                                };
                            });

                            return `
                                <div class="apexcharts-tooltip-title" style="font-size:12px;">${date}</div>
                                <div style="padding:5px; font-size:12px; color: #111;">
                                    ${filterData
                                        .map((item, index) => {
                                            return `
                                            <div style="padding:5px;">
                                                <span style="background:${
                                                    w.globals.colors[index] || '#000'
                                                }; width:10px; height:10px; border-radius:50%; display: inline-block; margin-right: 5px;"></span>
                                                <span>${item.name}: <b style="padding-left:5px;">${
                                                item.count
                                            }</b></span>
                                            </div>
                                        `;
                                        })
                                        .join('')}
                                        </div>
                            `;
                        },
                    },
                };
            },
        },
        mounted() {
            this.fetchChartData();
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        methods: {
            onSearchInput: debounce(function () {
                this.refresh();
            }, 500),

            formatDate(date) {
                if (date) {
                    const formattedDate = format(parseISO(date), this.userUnicodeDateFormat);
                    const formattedTime = format(parseISO(date), this.userTimeFormat);
                    return `${formattedDate} ${formattedTime}`;
                }
            },

            dateTimeRenderer(params) {
                return params.value === null ? 'N/A' : this.formatDate(params.value);
            },

            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },

            refresh() {
                this.$refs.grid?.refreshDataSource();
                this.fetchChartData();
            },

            async fetchSubscribers() {
                try {
                    this.busy = true;
                    const { data } = await API.get(`${STAYLIO_APP_STATISTICS_USER_ENDPOINT()}subscribers/`, {
                        params: {
                            search: this.subscriberSearch,
                        },
                    });
                    this.subscribersList = data.map(({ user }) => ({
                        text: `${user.id} - ${user.email}`,
                        value: user.id,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch subscribers' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchUsers() {
                try {
                    this.usersList = [];
                    this.busy = true;
                    const { data } = await API.get(`${STAYLIO_APP_STATISTICS_USER_ENDPOINT()}users/`, {
                        params: {
                            search: this.userSearch,
                        },
                    });
                    data.forEach((pair) => {
                        const user = {
                            text: `${pair.id} - ${pair.email}`,
                            value: pair.id,
                        };
                        this.usersList.push(user);
                    });
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch users' });
                } finally {
                    this.busy = false;
                }
            },
            selectSubscriber(text, value) {
                this.subscriberSearch = text;
                this.selectedSubscriber = value;
                this.subscribersList = [];
                this.refresh();
            },
            selectUser(text, value) {
                this.userSearch = text;
                this.selectedUser = value;
                this.usersList = [];
                this.refresh();
            },
            searchSubscribers: debounce(function (value) {
                this.subscriberSearch = value;
                this.selectedSubscriber = '';
                if (value === '') {
                    this.refresh();
                } else {
                    this.fetchSubscribers();
                }
            }, 500),
            searchUsers: debounce(function (value) {
                this.userSearch = value;
                this.selectedUser = '';
                if (value === '') {
                    this.refresh();
                } else {
                    this.fetchUsers();
                }
            }, 500),

            async fetchChartData() {
                const response = await API.get(`${STAYLIO_APP_STATISTICS_PUSH_NOTIFICATIONS_ENDPOINT()}chart-data/`, {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                    },
                });

                this.dates = response.data.list.map((item) => item.date);

                const metrics = [
                    'guest_push_notifications_sent',
                    'guest_push_notifications_delivered',
                    'guest_push_notifications_opened',
                    'guest_push_notifications_failed_to_deliver',
                ];

                const names = ['Sent', 'Delivered', 'Opened', 'Failed to Deliver'];

                const list = metrics.map((metric, index) => ({
                    name: names[index],
                    count: response.data.list.map((item) => item[metric]),
                }));

                this.chartData = list;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .subscribers-list-wrapper {
        top: 40px;
    }
    .subscribers-wrapper {
        min-width: 300px;
    }
    .title-header {
        margin-top: 0.5rem;
    }
    .text-muted {
        font-size: 0.875rem;
    }
</style>
